/* Countdown for the sitewide announcement bar (DEV-15226). Ticks every second.
   - `data-duration` (hours) → per-visitor countdown: end stored in sessionStorage
     so it survives page navigation, restarts when it reaches zero.
   - `data-end` (ISO date) → fixed countdown with the configured expiry behaviour:
     hide_timer → hides only this element; hide_message → hides the whole section */
class AnnouncementCountdown extends HTMLElement {
  static DAYS_THRESHOLD_HOURS = 100;
  static STORAGE_KEY = 'announcement-countdown-end';

  connectedCallback() {
    this._daysUnit = this.querySelector('[data-ab-unit="days"]');
    this._daysSep = this.querySelector('[data-ab-sep="days"]');
    this._daysEl = this.querySelector('[data-ab-days]');
    this._hoursEl = this.querySelector('[data-ab-hours]');
    this._minutesEl = this.querySelector('[data-ab-minutes]');
    this._secondsEl = this.querySelector('[data-ab-seconds]');

    if (!this._hoursEl || !this._minutesEl || !this._secondsEl) return;

    this._duration = Number(this.dataset.duration) * 3600 * 1000;

    if (this._duration > 0) {
      this._endTime = this._storedEnd() ?? this._restart();
      this._tick();
      this.hidden = false;
      this._interval = window.setInterval(() => this._tick(), 1000);
      return;
    }

    this._endTime = Date.parse(this.dataset.end ?? '');

    if (Number.isNaN(this._endTime)) {
      console.warn('[announcement-countdown] Invalid end date, expected ISO 8601 with offset:', this.dataset.end);
      return;
    }

    if (this._remainingSeconds() <= 0) {
      this._expire();
      return;
    }

    this._tick();
    this.hidden = false;
    this._interval = window.setInterval(() => this._tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this._interval);
  }

  _storedEnd() {
    try {
      const stored = Number(window.sessionStorage.getItem(AnnouncementCountdown.STORAGE_KEY));
      const now = Date.now();
      // Ignore values from a longer duration setting or already expired.
      return stored > now && stored <= now + this._duration ? stored : null;
    } catch {
      return null;
    }
  }

  _restart() {
    this._endTime = Date.now() + this._duration;
    try {
      window.sessionStorage.setItem(AnnouncementCountdown.STORAGE_KEY, String(this._endTime));
    } catch {}
    return this._endTime;
  }

  _remainingSeconds() {
    return Math.max(0, Math.floor((this._endTime - Date.now()) / 1000));
  }

  _tick() {
    if (this._duration > 0 && this._remainingSeconds() === 0) this._restart();

    const remaining = this._remainingSeconds();

    if (remaining === 0) {
      window.clearInterval(this._interval);
      this._expire();
      return;
    }

    const totalHours = Math.floor(remaining / 3600);
    const showDays = totalHours >= AnnouncementCountdown.DAYS_THRESHOLD_HOURS;
    const days = Math.floor(totalHours / 24);
    const hours = showDays ? totalHours % 24 : totalHours;
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;

    this._toggleDays(showDays);

    if (showDays && this._daysEl) this._daysEl.textContent = AnnouncementCountdown.pad(days);
    this._hoursEl.textContent = AnnouncementCountdown.pad(hours);
    this._minutesEl.textContent = AnnouncementCountdown.pad(minutes);
    this._secondsEl.textContent = AnnouncementCountdown.pad(seconds);
  }

  _toggleDays(show) {
    if (this._daysUnit) this._daysUnit.hidden = !show;
    if (this._daysSep) this._daysSep.hidden = !show;
  }

  _expire() {
    this.hidden = true;

    if (this.dataset.expired === 'hide_message') {
      this.closest('.shopify-section')?.classList.add('announcement-bar--expired');
    }
  }

  static pad(value) {
    return String(value).padStart(2, '0');
  }
}

if (!window.customElements.get('announcement-countdown')) {
  window.customElements.define('announcement-countdown', AnnouncementCountdown);
}
